import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Plus, Trash2, Check, X, Ban, Sparkles } from "lucide-react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CANCEL_REASONS = ["customer_request", "driver_unavailable", "vehicle_issue", "overbooking", "flight_cancelled", "weather", "duplicate", "other"];

const STATUSES = ["", "pending", "pending_confirmation", "pending_payment", "confirmed", "assigned", "arrived", "on_board"];

const Kpi = ({ label, value, testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testId}>
    <p className="text-xs text-slate-500">{label.replace(/_/g, " ")}</p>
    <p className="mt-1.5 font-display text-2xl text-slate-900" data-testid={testId ? `${testId}-value` : undefined}>
      {value}
    </p>
  </div>
);

const APPROVAL_STYLE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  suspended: "bg-slate-100 text-slate-600 border-slate-200",
};

const digits = (s) => String(s || "").replace(/\D/g, "");

// A booking belongs in the Archive tab (and must be hidden from Active) when it is
// completed/cancelled, or its pickup date is more than 24 hours in the past — regardless of status.
const isArchived = (b) => {
  if (["completed", "cancelled"].includes(b.status)) return true;
  const t = Date.parse(b.pickup_at);
  if (!Number.isNaN(t) && t < Date.now() - 24 * 60 * 60 * 1000) return true;
  return false;
};

const matches = (b, term) => {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  const d = digits(q);
  if (d.length >= 4 && digits(b.phone).includes(d)) return true;
  return [b.code, b.customer_name, b.email, b.agency_company, b.destination?.name]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(q));
};

export default function AdminPanel() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleClasses, setVehicleClasses] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [dispatch, setDispatch] = useState(null);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [period, setPeriod] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("");
  const [hotelQ, setHotelQ] = useState("");
  const [archiveView, setArchiveView] = useState(false);
  const [archived, setArchived] = useState([]);
  const [archiveQ, setArchiveQ] = useState("");
  const [archiveFrom, setArchiveFrom] = useState("");
  const [archiveTo, setArchiveTo] = useState("");
  const [feasibility, setFeasibility] = useState({});
  const [newAgency, setNewAgency] = useState({ email: "", password: "", company: "", contact_name: "", phone: "", discount_pct: 10, credit_limit: 5000 });
  const [newDriver, setNewDriver] = useState({ name: "", email: "", password: "", phone: "", plate: "", car: "", commission_pct: 25 });
  const [newVehicle, setNewVehicle] = useState({ model: "", plate: "", class_id: "", active: true });
  const [newHotel, setNewHotel] = useState({ name: "", area: "", km_ayt: "", km_gzp: "", lat: "", lng: "" });
  const [partners, setPartners] = useState([]);
  const [newPartner, setNewPartner] = useState({ name: "", company: "", contact_email: "", pricing_mode: "standard", markup_pct: 0, credit_limit: 0, webhook_url: "" });
  const [revealedKey, setRevealedKey] = useState(null);
  const [partnerBookings, setPartnerBookings] = useState({});
  const [cancellations, setCancellations] = useState([]);
  const [pendingReturns, setPendingReturns] = useState([]);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      if (tab === "overview") setOverview((await api.get("/admin/overview")).data);
      if (tab === "bookings") {
        const { data } = await api.get("/admin/bookings", { params: { status } });
        setBookings(data.bookings || []);
        setVendors((await api.get("/admin/vendors")).data.vendors || []);
        if (archiveView) setArchived((await api.get("/admin/bookings/archive")).data.bookings || []);
      }
      if (tab === "drivers") setVendors((await api.get("/admin/vendors")).data.vendors || []);
      if (tab === "vehicles") {
        setVehicles((await api.get("/admin/vehicles")).data.vehicles || []);
        const { data } = await api.get("/vehicle-classes");
        setVehicleClasses((data.classes || []).map((c) => ({ id: c.class_id, name: c.name })));
      }
      if (tab === "hotels") setHotels((await api.get("/admin/hotels", { params: { q: hotelQ, limit: 60 } })).data.hotels || []);
      if (tab === "pricing") {
        const { data } = await api.get("/admin/pricing");
        setPricing(data.classes || data.pricing || []);
      }
      if (tab === "agencies") setAgencies((await api.get("/admin/agencies")).data.agencies || []);
      if (tab === "partners") setPartners((await api.get("/admin/partners")).data.partners || []);
      if (tab === "cancellations") {
        const { data } = await api.get("/admin/cancellations");
        setCancellations(data.cancellations || data.items || data.bookings || []);
      }
      if (tab === "returns") {
        const [r, vd] = await Promise.all([api.get("/admin/returns/pending"), api.get("/admin/vendors")]);
        setPendingReturns(r.data.returns || r.data.bookings || r.data.items || []);
        setVendors(vd.data.vendors || []);
      }
      if (tab === "invoices") {
        const { data } = await api.get("/admin/invoices", { params: { month } });
        setInvoices(data.invoices || []);
        setPeriod(data.period || month);
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [tab, status, month, hotelQ, archiveView, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const wrap = (fn) => async (...args) => {
    try {
      await fn(...args);
      toast.success(t("admin.saved"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const assign = wrap((bookingId, vendorId) => api.post(`/admin/bookings/${bookingId}/assign`, { vendor_id: vendorId }));
  const unassign = wrap((bookingId) => api.post(`/admin/bookings/${bookingId}/unassign`));
  const noShow = wrap((bookingId) => api.post(`/admin/bookings/${bookingId}/no-show`));
  const confirmBooking = wrap((bookingId) => api.post(`/admin/bookings/${bookingId}/confirm`));
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("customer_request");
  const [cancelNote, setCancelNote] = useState("");
  const submitCancel = async () => {
    try {
      await api.post(`/admin/bookings/${cancelTarget.booking_id}/cancel`, { reason_code: cancelReason, note: cancelNote || null });
      toast.success(t("admin.cancelled"));
      setCancelTarget(null);
      setCancelNote("");
      setCancelReason("customer_request");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const saveCommission = wrap((v, pct) => api.post(`/admin/vendors/${v.user_id}/commission`, { commission_pct: Number(pct) }));
  const assignReturn = wrap((bookingId, vendorId) => api.post(`/admin/bookings/${bookingId}/assign-return`, { vendor_id: vendorId }));
  const setApproval = wrap((v, s) => api.post(`/admin/vendors/${v.user_id}/approval`, { status: s }));
  const deleteDriver = wrap((v) => api.delete(`/admin/vendors/${v.user_id}`));
  const savePricing = wrap((p) => api.post("/admin/pricing", { class_id: p.class_id, base: Number(p.base), per_km: Number(p.per_km), min_fare: Number(p.min_fare), fleet_size: Number(p.fleet_size) }));
  const toggleAgency = wrap((a) => api.patch(`/admin/agencies/${a.user_id}`, { active: !a.active }));
  const toggleVehicle = wrap((v) => api.patch(`/admin/vehicles/${v.id}`, { active: !v.active }));
  const deleteVehicle = wrap((v) => api.delete(`/admin/vehicles/${v.id}`));
  const toggleHotel = wrap((h) => api.patch(`/admin/hotels/${h.id}`, { active: !h.active }));

  const archivedFiltered = archived.filter((b) => {
    if (!matches(b, archiveQ)) return false;
    const t = Date.parse(b.pickup_at);
    if (archiveFrom && (Number.isNaN(t) || t < Date.parse(archiveFrom))) return false;
    if (archiveTo && (Number.isNaN(t) || t > Date.parse(archiveTo) + 24 * 60 * 60 * 1000 - 1)) return false;
    return true;
  });

  const createDriver = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/vendors", { ...newDriver, phone: newDriver.phone || null, commission_pct: Number(newDriver.commission_pct) });
      toast.success(t("admin.saved"));
      setNewDriver({ name: "", email: "", password: "", phone: "", plate: "", car: "", commission_pct: 25 });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const createVehicle = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/vehicles", { ...newVehicle });
      toast.success(t("admin.saved"));
      setNewVehicle({ model: "", plate: "", class_id: "", active: true });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const createHotel = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/hotels", {
        name: newHotel.name,
        area: newHotel.area,
        km_ayt: Number(newHotel.km_ayt),
        km_gzp: Number(newHotel.km_gzp),
        lat: newHotel.lat ? Number(newHotel.lat) : null,
        lng: newHotel.lng ? Number(newHotel.lng) : null,
      });
      toast.success(t("admin.saved"));
      setNewHotel({ name: "", area: "", km_ayt: "", km_gzp: "", lat: "", lng: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const checkFeasibility = async (bookingId, vendorId, code) => {
    if (!vendorId) return;
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/dispatch-feasibility`, { vendor_id: vendorId });
      setFeasibility((f) => ({ ...f, [code]: data }));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadDispatch = async () => {
    setDispatchLoading(true);
    try {
      const [d, v] = await Promise.all([api.get("/admin/dispatch/suggestions"), api.get("/admin/vendors")]);
      setDispatch(d.data);
      setVendors(v.data.vendors || []);
    } catch (e) {
      toast.error(apiError(e));
    }
    setDispatchLoading(false);
  };

  const createAgency = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/agencies", { ...newAgency, phone: newAgency.phone || null, discount_pct: Number(newAgency.discount_pct), credit_limit: Number(newAgency.credit_limit) });
      toast.success(t("admin.saved"));
      setNewAgency({ email: "", password: "", company: "", contact_name: "", phone: "", discount_pct: 10, credit_limit: 5000 });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const partnerId = (p) => p.partner_id || p.id;
  const partnerKeyOf = (data) => data?.api_key || data?.partner?.api_key || data?.key || null;

  const createPartner = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/partners", {
        ...newPartner,
        company: newPartner.company || null,
        contact_email: newPartner.contact_email || null,
        webhook_url: newPartner.webhook_url || null,
        markup_pct: Number(newPartner.markup_pct) || 0,
        credit_limit: Number(newPartner.credit_limit) || 0,
      });
      const key = partnerKeyOf(data);
      const created = data?.partner || data;
      if (key) setRevealedKey({ id: partnerId(created) || "new", key });
      toast.success(t("admin.saved"));
      setNewPartner({ name: "", company: "", contact_email: "", pricing_mode: "standard", markup_pct: 0, credit_limit: 0, webhook_url: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const togglePartner = wrap((p) => api.patch(`/admin/partners/${partnerId(p)}`, { active: !(p.active !== false) }));
  const deletePartner = wrap((p) => api.delete(`/admin/partners/${partnerId(p)}`));

  const rotatePartnerKey = async (p) => {
    try {
      const { data } = await api.post(`/admin/partners/${partnerId(p)}/rotate-key`);
      const key = partnerKeyOf(data);
      if (key) setRevealedKey({ id: partnerId(p), key });
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const loadPartnerBookings = async (p) => {
    const id = partnerId(p);
    if (partnerBookings[id]) {
      setPartnerBookings((s) => { const n = { ...s }; delete n[id]; return n; });
      return;
    }
    try {
      const { data } = await api.get(`/admin/partners/${id}/bookings`, { params: { limit: 20 } });
      setPartnerBookings((s) => ({ ...s, [id]: data.bookings || [] }));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const copyKey = (key) => {
    navigator.clipboard?.writeText(key).then(() => toast.success(t("admin.keyCopied"))).catch(() => {});
  };

  if (!isAdmin)
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <p className="mx-auto max-w-3xl px-5 py-24 text-slate-600" data-testid="admin-no-access">
          {t("admin.noAccess")}
        </p>
      </div>
    );

  const tabs = [
    ["overview", t("admin.overview")],
    ["bookings", t("admin.bookings")],
    ["dispatch", t("ops.dispatch")],
    ["drivers", t("admin.drivers")],
    ["vehicles", t("ops.vehicles")],
    ["hotels", t("ops.hotels")],
    ["pricing", t("admin.pricing")],
    ["agencies", t("admin.agencies")],
    ["partners", t("admin.partners")],
    ["returns", t("admin.returnsTab")],
    ["cancellations", t("admin.cancellationsTab")],
    ["invoices", t("admin.invoices")],
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-10 lg:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-display text-3xl tracking-tight sm:text-4xl" data-testid="admin-title">
            {t("admin.title")}
          </h1>
          <Button variant="outline" data-testid="admin-refresh-btn" onClick={load}>
            <RefreshCw size={15} className="mr-1.5" /> {t("common.refresh")}
          </Button>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 rounded-md bg-slate-100 p-1 text-sm">
          {tabs.map(([v, l]) => (
            <button
              key={v}
              data-testid={`admin-tab-${v}`}
              onClick={() => setTab(v)}
              className={`rounded px-4 py-1.5 font-medium transition-colors duration-200 ${
                tab === v ? "bg-white text-sea shadow-sm" : "text-slate-500"
              }`}
            >
              {l}
            </button>
          ))}
          <button
            data-testid="admin-tab-calendar"
            onClick={() => navigate("/admin/calendar")}
            className="rounded px-4 py-1.5 font-medium text-slate-500 transition-colors duration-200 hover:text-sea"
          >
            {t("fc.calendar")} ↗
          </button>
          <button
            data-testid="admin-tab-forecast"
            onClick={() => navigate("/admin/forecast")}
            className="rounded px-4 py-1.5 font-medium text-slate-500 transition-colors duration-200 hover:text-sea"
          >
            {t("fc.forecast")} ↗
          </button>
          <button
            data-testid="admin-tab-driver-availability"
            onClick={() => navigate("/admin/driver-availability")}
            className="rounded px-4 py-1.5 font-medium text-slate-500 transition-colors duration-200 hover:text-sea"
          >
            {t("sch.driverCalendar")} ↗
          </button>
        </div>

        {tab === "overview" && overview && (
          <div className="mt-8 space-y-8" data-testid="admin-overview">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Kpi label={t("admin.totalBookings")} value={overview.total_bookings ?? 0} testId="kpi-total-bookings" />
              <Kpi label={t("admin.unassigned")} value={overview.unassigned ?? 0} testId="kpi-unassigned" />
              <Kpi label={t("admin.revenue")} value={eur(overview.revenue_eur)} testId="kpi-revenue" />
              <Kpi label={t("admin.commissionEur")} value={eur(overview.commission_eur)} testId="kpi-commission" />
              <Kpi label={t("admin.payout")} value={eur(overview.driver_payout_eur)} testId="kpi-payout" />
              <Kpi label={t("admin.drivers")} value={overview.vendors ?? 0} testId="kpi-drivers" />
              <Kpi label={t("admin.agencies")} value={overview.agencies ?? 0} testId="kpi-agencies" />
              <Kpi label={t("admin.fleet")} value={overview.fleet_total ?? 0} testId="kpi-fleet" />
            </div>

            <section>
              <p className="label-eyebrow">{t("common.status")}</p>
              <div className="mt-4 flex flex-wrap gap-3" data-testid="admin-status-breakdown">
                {Object.entries(overview.by_status || {}).map(([s, n]) => (
                  <div key={s} data-testid={`status-count-${s}`} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <StatusBadge status={s} />
                    <span className="font-display text-lg">{n}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <p className="label-eyebrow">{t("admin.utilization")}</p>
              <div className="mt-4 space-y-2" data-testid="admin-utilization">
                {(overview.utilization || []).map((u) => (
                  <div key={u.class_id} data-testid={`utilization-${u.class_id}`} className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                      <span className="font-medium">{u.name}</span>
                      <span className="text-slate-500">{u.in_use}/{u.fleet_size} · {u.utilization_pct}%</span>
                    </div>
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-sea transition-all duration-500" style={{ width: `${Math.min(100, u.utilization_pct)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {tab === "bookings" && (
          <div className="mt-8">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex rounded-md bg-slate-100 p-0.5 text-sm" data-testid="bookings-view-toggle">
                <button data-testid="bookings-active-btn" onClick={() => setArchiveView(false)} className={`rounded px-3 py-1 font-medium ${!archiveView ? "bg-white text-sea shadow-sm" : "text-slate-500"}`}>{t("sch.active")}</button>
                <button data-testid="bookings-archive-btn" onClick={() => setArchiveView(true)} className={`rounded px-3 py-1 font-medium ${archiveView ? "bg-white text-sea shadow-sm" : "text-slate-500"}`}>{t("sch.archive")}</button>
              </div>
              {!archiveView && <Input data-testid="admin-search-input" placeholder={t("admin.search")} value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />}
              {!archiveView && (
                <select data-testid="admin-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s ? t(`status.${s}`) : t("admin.allStatus")}</option>
                  ))}
                </select>
              )}
              {archiveView && (
                <>
                  <Input data-testid="archive-search-input" placeholder={t("admin.search")} value={archiveQ} onChange={(e) => setArchiveQ(e.target.value)} className="max-w-xs" />
                  <input type="date" data-testid="archive-date-from" value={archiveFrom} onChange={(e) => setArchiveFrom(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" />
                  <span className="text-sm text-slate-400">→</span>
                  <input type="date" data-testid="archive-date-to" value={archiveTo} onChange={(e) => setArchiveTo(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" />
                  {(archiveQ || archiveFrom || archiveTo) && (
                    <button data-testid="archive-clear-filters" onClick={() => { setArchiveQ(""); setArchiveFrom(""); setArchiveTo(""); }} className="text-sm font-medium text-sea hover:underline">
                      {t("common.clear")}
                    </button>
                  )}
                </>
              )}
            </div>

            {archiveView ? (
              <div className="mt-6 space-y-3" data-testid="admin-archive-list">
                {archivedFiltered.map((b) => (
                  <div key={b.booking_id} data-testid={`admin-archive-${b.code}`} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5 opacity-90">
                    <div>
                      <p className="label-eyebrow">{b.code}</p>
                      <p className="mt-1.5 text-sm font-medium">{b.origin?.name} → {b.destination?.name}</p>
                      <p className="mt-1 text-xs text-slate-500">{b.customer_name} · {b.pickup_at?.replace("T", " · ").slice(0, 18)}</p>
                    </div>
                    <div className="text-right">
                      <StatusBadge status={b.status} />
                      <p className="mt-2 text-sm font-medium">{eur(b.price_eur)}</p>
                    </div>
                  </div>
                ))}
                {archivedFiltered.length === 0 && <p className="text-slate-500" data-testid="archive-empty">{t("common.empty")}</p>}
              </div>
            ) : (
            <div className="mt-6 space-y-3" data-testid="admin-bookings-list">
              {bookings.filter((b) => matches(b, q) && !isArchived(b)).map((b) => (
                <div key={b.booking_id} data-testid={`admin-booking-${b.code}`} className="grid gap-4 rounded-lg border border-slate-200 bg-white p-5 md:grid-cols-12">
                  <div className="md:col-span-5">
                    <p className="label-eyebrow">{b.code}</p>
                    <p className="mt-1.5 text-sm font-medium">{b.origin?.name} → {b.destination?.name}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {b.customer_name} · {b.phone} · {b.pickup_at?.replace("T", " · ").slice(0, 18)}
                      {b.agency_company ? ` · ${b.agency_company}` : ""}
                    </p>
                  </div>
                  <div className="md:col-span-3">
                    <StatusBadge status={b.status} />
                    <p className="mt-2 text-sm font-medium">{eur(b.price_eur)}</p>
                    <p className="text-xs text-slate-500">{b.vehicle_class_name}</p>
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-xs">{t("admin.assign")}</Label>
                    <select
                      data-testid={`assign-select-${b.code}`}
                      value={b.vendor_id || ""}
                      onChange={(e) => e.target.value && assign(b.booking_id, e.target.value)}
                      className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                    >
                      <option value="">—</option>
                      {vendors.map((v) => (
                        <option key={v.user_id} value={v.user_id}>{v.name || v.email}</option>
                      ))}
                    </select>
                    {!["completed", "cancelled"].includes(b.status) && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {["pending", "pending_confirmation", "pending_payment"].includes(b.status) && (
                          <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`confirm-${b.code}-btn`} onClick={() => confirmBooking(b.booking_id)}>
                            {t("admin.confirmBtn")}
                          </Button>
                        )}
                        {b.vendor_id && (
                          <Button size="sm" variant="outline" data-testid={`unassign-${b.code}-btn`} onClick={() => unassign(b.booking_id)}>
                            {t("ops.unassign")}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`noshow-${b.code}-btn`} onClick={() => noShow(b.booking_id)}>
                          {t("ops.noShow")}
                        </Button>
                        <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`cancel-${b.code}-btn`} onClick={() => setCancelTarget(b)}>
                          <Ban size={13} className="mr-1" /> {t("admin.cancelBtn")}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {bookings.filter((b) => matches(b, q) && !isArchived(b)).length === 0 && <p className="text-slate-500">{t("common.empty")}</p>}
            </div>
            )}
          </div>
        )}

        {tab === "dispatch" && (
          <div className="mt-8" data-testid="admin-dispatch">
            <Button data-testid="dispatch-load-btn" className="bg-sea hover:bg-sea-dark" onClick={loadDispatch} disabled={dispatchLoading}>
              <Sparkles size={15} className="mr-1.5" /> {dispatchLoading ? t("common.loading") : t("ops.loadSuggestions")}
            </Button>
            {dispatch?.summary && (
              <div className="mt-6 rounded-xl border border-sea/20 bg-sea/5 p-5" data-testid="dispatch-summary">
                <p className="label-eyebrow">{t("ops.suggestions")}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{dispatch.summary}</p>
              </div>
            )}
            <div className="mt-6 space-y-3" data-testid="dispatch-list">
              {(dispatch?.suggestions || []).map((s) => (
                <div key={s.booking_id} data-testid={`dispatch-${s.code}`} className="grid gap-4 rounded-lg border border-slate-200 bg-white p-5 md:grid-cols-12">
                  <div className="md:col-span-7">
                    <p className="label-eyebrow">{s.code}</p>
                    <p className="mt-1.5 text-sm font-medium">{s.origin} → {s.destination}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {s.pickup_at?.replace("T", " · ").slice(0, 18)} · {s.vehicle_class_name}
                      {s.flight_number ? ` · ${s.flight_number}` : ""} · {s.pax} pax
                    </p>
                    {s.reason && <p className="mt-2 text-xs text-slate-500">{s.reason}</p>}
                  </div>
                  <div className="md:col-span-5">
                    <Label className="text-xs">{t("admin.assign")}</Label>
                    <select
                      data-testid={`dispatch-assign-${s.code}`}
                      defaultValue=""
                      onChange={(e) => e.target.value && assign(s.booking_id, e.target.value)}
                      className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                    >
                      <option value="">—</option>
                      {vendors.map((v) => (
                        <option key={v.user_id} value={v.user_id}>{v.name || v.email}</option>
                      ))}
                    </select>
                    <div className="mt-2 flex items-center gap-2">
                      <select
                        data-testid={`feasibility-vendor-${s.code}`}
                        defaultValue=""
                        onChange={(e) => e.target.value && checkFeasibility(s.booking_id, e.target.value, s.code)}
                        className="h-9 flex-1 rounded-md border border-slate-200 bg-white px-2 text-xs"
                      >
                        <option value="">{t("sch.feasibility")}…</option>
                        {vendors.map((v) => (
                          <option key={v.user_id} value={v.user_id}>{v.name || v.email}</option>
                        ))}
                      </select>
                    </div>
                    {feasibility[s.code] && (
                      <div data-testid={`feasibility-result-${s.code}`} className={`mt-2 rounded-md px-2 py-1.5 text-[11px] ${(feasibility[s.code].feasible ?? feasibility[s.code].ok) ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                        {(feasibility[s.code].feasible ?? feasibility[s.code].ok) ? `✓ ${t("sch.feasible")}` : `✕ ${t("sch.notFeasible")}`}
                        {feasibility[s.code].reason ? ` · ${feasibility[s.code].reason}` : ""}
                        {feasibility[s.code].eta_min ? ` · ${feasibility[s.code].eta_min} ${t("results.min")}` : ""}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {dispatch && (dispatch.suggestions || []).length === 0 && <p className="text-slate-500" data-testid="dispatch-empty">{t("ops.noSuggestions")}</p>}
            </div>
          </div>
        )}

        {tab === "drivers" && (
          <div className="mt-8 grid gap-8 lg:grid-cols-12">
            <form onSubmit={createDriver} data-testid="driver-form" className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 lg:col-span-4">
              <p className="label-eyebrow">{t("ops.addDriver")}</p>
              {[
                ["name", t("ops.name")],
                ["email", t("auth.email")],
                ["password", t("auth.password")],
                ["phone", t("auth.phone")],
                ["plate", t("ops.plate")],
                ["car", t("ops.car")],
                ["commission_pct", t("admin.commission")],
              ].map(([field, label]) => (
                <div key={field}>
                  <Label className="text-xs">{label}</Label>
                  <Input
                    data-testid={`driver-${field}-input`}
                    type={field === "password" ? "password" : field === "commission_pct" ? "number" : "text"}
                    required={["name", "email", "password", "phone", "plate", "car"].includes(field)}
                    value={newDriver[field]}
                    onChange={(e) => setNewDriver({ ...newDriver, [field]: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              ))}
              <Button data-testid="driver-create-btn" type="submit" className="w-full bg-sea hover:bg-sea-dark">
                <Plus size={15} className="mr-1.5" /> {t("ops.create")}
              </Button>
            </form>

            <div className="space-y-3 lg:col-span-8" data-testid="admin-drivers-list">
              {vendors.map((v) => (
                <div key={v.user_id} data-testid={`admin-driver-${v.user_id}`} className="rounded-lg border border-slate-200 bg-white p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{v.name || v.email}</p>
                        <span
                          data-testid={`driver-approval-${v.user_id}`}
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${APPROVAL_STYLE[v.approval_status] || APPROVAL_STYLE.pending}`}
                        >
                          {t(`ops.${v.approval_status || "pending"}`)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">{v.email} · {v.phone || "—"} · {v.car || "—"} {v.plate ? `· ${v.plate}` : ""}</p>
                      <p className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
                        <span>{t("admin.jobs")}: <b>{v.jobs ?? 0}</b></span>
                        <span>{t("driver.completed")}: <b>{v.completed ?? 0}</b></span>
                        <span>{t("admin.gross")}: <b>{eur(v.gross_eur)}</b></span>
                        <span>{t("admin.payout")}: <b>{eur(v.payout_eur)}</b></span>
                      </p>
                    </div>
                    <div className="flex items-end gap-2">
                      <div>
                        <Label className="text-xs">{t("admin.commission")}</Label>
                        <Input
                          data-testid={`commission-input-${v.user_id}`}
                          type="number"
                          defaultValue={v.commission_pct ?? 25}
                          className="mt-1.5 w-24"
                          onBlur={(e) => { if (Number(e.target.value) !== (v.commission_pct ?? 25)) saveCommission(v, e.target.value); }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`approve-${v.user_id}-btn`}
                      disabled={v.approval_status === "approved"}
                      onClick={() => setApproval(v, "approved")}
                    >
                      <Check size={14} className="mr-1" /> {t("ops.approve")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                      data-testid={`reject-${v.user_id}-btn`}
                      disabled={v.approval_status === "rejected"}
                      onClick={() => setApproval(v, "rejected")}
                    >
                      <X size={14} className="mr-1" /> {t("ops.reject")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="disabled:opacity-50"
                      data-testid={`suspend-${v.user_id}-btn`}
                      disabled={v.approval_status === "suspended"}
                      onClick={() => setApproval(v, "suspended")}
                    >
                      <Ban size={14} className="mr-1" /> {t("ops.suspend")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-600 hover:bg-rose-50"
                      data-testid={`delete-driver-${v.user_id}-btn`}
                      onClick={() => window.confirm(t("ops.confirmDel")) && deleteDriver(v)}
                    >
                      <Trash2 size={14} className="mr-1" /> {t("ops.del")}
                    </Button>
                  </div>
                </div>
              ))}
              {vendors.length === 0 && <p className="text-slate-500">{t("common.empty")}</p>}
            </div>
          </div>
        )}

        {tab === "vehicles" && (
          <div className="mt-8 grid gap-8 lg:grid-cols-12">
            <form onSubmit={createVehicle} data-testid="vehicle-form" className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 lg:col-span-4">
              <p className="label-eyebrow">{t("ops.addVehicle")}</p>
              <div>
                <Label className="text-xs">{t("ops.model")}</Label>
                <Input data-testid="vehicle-model-input" required value={newVehicle.model} onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs">{t("ops.plate")}</Label>
                <Input data-testid="vehicle-plate-input" required value={newVehicle.plate} onChange={(e) => setNewVehicle({ ...newVehicle, plate: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs">{t("ops.vclass")}</Label>
                <select data-testid="vehicle-class-select" required value={newVehicle.class_id} onChange={(e) => setNewVehicle({ ...newVehicle, class_id: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                  <option value="">—</option>
                  {vehicleClasses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <Button data-testid="vehicle-create-btn" type="submit" className="w-full bg-sea hover:bg-sea-dark">
                <Plus size={15} className="mr-1.5" /> {t("ops.create")}
              </Button>
            </form>

            <div className="space-y-3 lg:col-span-8" data-testid="admin-vehicles-list">
              {vehicles.map((v) => (
                <div key={v.id} data-testid={`admin-vehicle-${v.id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5">
                  <div>
                    <p className="text-sm font-medium">{v.model} · {v.plate}</p>
                    <p className="text-xs text-slate-500">{v.class_name || v.class_id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" data-testid={`vehicle-toggle-${v.id}`} onClick={() => toggleVehicle(v)}>
                      {v.active ? t("ops.active") : t("ops.inactive")}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" data-testid={`vehicle-delete-${v.id}`} onClick={() => window.confirm(t("ops.confirmDel")) && deleteVehicle(v)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
              {vehicles.length === 0 && <p className="text-slate-500" data-testid="vehicles-empty">{t("ops.noVehicles")}</p>}
            </div>
          </div>
        )}

        {tab === "hotels" && (
          <div className="mt-8 grid gap-8 lg:grid-cols-12">
            <form onSubmit={createHotel} data-testid="hotel-form" className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 lg:col-span-4">
              <p className="label-eyebrow">{t("ops.addHotel")}</p>
              {[
                ["name", t("ops.name"), "text"],
                ["area", t("ops.area"), "text"],
                ["km_ayt", t("ops.kmAyt"), "number"],
                ["km_gzp", t("ops.kmGzp"), "number"],
                ["lat", "Lat", "number"],
                ["lng", "Lng", "number"],
              ].map(([field, label, type]) => (
                <div key={field}>
                  <Label className="text-xs">{label}</Label>
                  <Input
                    data-testid={`hotel-${field}-input`}
                    type={type}
                    required={["name", "area", "km_ayt", "km_gzp"].includes(field)}
                    value={newHotel[field]}
                    onChange={(e) => setNewHotel({ ...newHotel, [field]: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              ))}
              <Button data-testid="hotel-create-btn" type="submit" className="w-full bg-sea hover:bg-sea-dark">
                <Plus size={15} className="mr-1.5" /> {t("ops.create")}
              </Button>
            </form>

            <div className="lg:col-span-8">
              <Input data-testid="hotel-search-input" placeholder={t("ops.searchHotel")} value={hotelQ} onChange={(e) => setHotelQ(e.target.value)} className="max-w-sm" />
              <div className="mt-4 space-y-3" data-testid="admin-hotels-list">
                {hotels.map((h) => (
                  <div key={h.id} data-testid={`admin-hotel-${h.id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5">
                    <div>
                      <p className="text-sm font-medium">{h.name}</p>
                      <p className="text-xs text-slate-500">{h.area} · AYT {h.km_ayt}km · GZP {h.km_gzp}km</p>
                    </div>
                    <Button size="sm" variant="outline" data-testid={`hotel-toggle-${h.id}`} onClick={() => toggleHotel(h)}>
                      {h.active === false ? t("ops.inactive") : t("ops.active")}
                    </Button>
                  </div>
                ))}
                {hotels.length === 0 && <p className="text-slate-500" data-testid="hotels-empty">{t("ops.noHotels")}</p>}
              </div>
            </div>
          </div>
        )}

        {tab === "pricing" && (
          <div className="mt-8 space-y-3" data-testid="admin-pricing-list">
            {pricing.map((p, idx) => (
              <div key={p.class_id} data-testid={`pricing-row-${p.class_id}`} className="grid items-end gap-4 rounded-lg border border-slate-200 bg-white p-5 md:grid-cols-12">
                <p className="text-sm font-medium md:col-span-3">{p.name || p.class_id}</p>
                {[["base", t("admin.base")], ["per_km", t("admin.perKm")], ["min_fare", t("admin.minFare")], ["fleet_size", t("admin.fleet")]].map(([field, label]) => (
                  <div key={field} className="md:col-span-2">
                    <Label className="text-xs">{label}</Label>
                    <Input
                      data-testid={`pricing-${p.class_id}-${field}`}
                      type="number"
                      step="0.01"
                      value={p[field] ?? 0}
                      onChange={(e) => { const next = [...pricing]; next[idx] = { ...p, [field]: e.target.value }; setPricing(next); }}
                      className="mt-1.5"
                    />
                  </div>
                ))}
                <div className="md:col-span-1">
                  <Button data-testid={`pricing-save-${p.class_id}`} size="sm" className="bg-sea hover:bg-sea-dark" onClick={() => savePricing(p)}>
                    {t("admin.save")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "agencies" && (
          <div className="mt-8 grid gap-8 md:grid-cols-12">
            <form onSubmit={createAgency} data-testid="agency-form" className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 md:col-span-5">
              <p className="label-eyebrow">{t("admin.newAgency")}</p>
              {[["company", t("admin.company")], ["contact_name", t("admin.contact")], ["email", t("auth.email")], ["password", t("auth.password")], ["phone", t("auth.phone")], ["discount_pct", t("admin.discount")], ["credit_limit", t("admin.credit")]].map(([field, label]) => (
                <div key={field}>
                  <Label className="text-xs">{label}</Label>
                  <Input
                    data-testid={`agency-${field}-input`}
                    type={field === "password" ? "password" : field.match(/pct|limit/) ? "number" : "text"}
                    required={["company", "contact_name", "email", "password"].includes(field)}
                    value={newAgency[field]}
                    onChange={(e) => setNewAgency({ ...newAgency, [field]: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              ))}
              <Button data-testid="agency-create-btn" type="submit" className="w-full bg-sea hover:bg-sea-dark">{t("admin.create")}</Button>
            </form>

            <div className="space-y-3 md:col-span-7" data-testid="admin-agencies-list">
              {agencies.map((a) => (
                <div key={a.user_id} data-testid={`admin-agency-${a.user_id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5">
                  <div>
                    <p className="text-sm font-medium">{a.company}</p>
                    <p className="text-xs text-slate-500">{a.email} · {t("admin.discount")} {a.discount_pct}% · {t("admin.credit")} {eur(a.credit_limit)}</p>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`agency-toggle-${a.user_id}`} onClick={() => toggleAgency(a)}>
                    {a.active === false ? "Activate" : "Deactivate"}
                  </Button>
                </div>
              ))}
              {agencies.length === 0 && <p className="text-slate-500">{t("common.empty")}</p>}
            </div>
          </div>
        )}

        {tab === "partners" && (
          <div className="mt-8 grid gap-8 md:grid-cols-12">
            <form onSubmit={createPartner} data-testid="partner-form" className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 md:col-span-5">
              <p className="label-eyebrow">{t("admin.newPartner")}</p>
              {[["name", t("admin.partnerName")], ["company", t("admin.company")], ["contact_email", t("auth.email")], ["webhook_url", t("admin.webhook")]].map(([field, label]) => (
                <div key={field}>
                  <Label className="text-xs">{label}</Label>
                  <Input
                    data-testid={`partner-${field}-input`}
                    type="text"
                    required={field === "name"}
                    value={newPartner[field]}
                    onChange={(e) => setNewPartner({ ...newPartner, [field]: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              ))}
              <div>
                <Label className="text-xs">{t("admin.pricingMode")}</Label>
                <select
                  data-testid="partner-pricing-mode"
                  value={newPartner.pricing_mode}
                  onChange={(e) => setNewPartner({ ...newPartner, pricing_mode: e.target.value })}
                  className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="standard">{t("admin.pmStandard")}</option>
                  <option value="agency">{t("admin.pmAgency")}</option>
                  <option value="markup">{t("admin.pmMarkup")}</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("admin.markup")}</Label>
                  <Input data-testid="partner-markup-input" type="number" min={0} max={100} value={newPartner.markup_pct} onChange={(e) => setNewPartner({ ...newPartner, markup_pct: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs">{t("admin.credit")}</Label>
                  <Input data-testid="partner-credit-input" type="number" min={0} value={newPartner.credit_limit} onChange={(e) => setNewPartner({ ...newPartner, credit_limit: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <Button data-testid="partner-create-btn" type="submit" className="w-full bg-sea hover:bg-sea-dark">{t("admin.create")}</Button>
              <p className="text-xs text-slate-500">{t("admin.partnerDocsHint")} <code className="rounded bg-slate-100 px-1">/api/partner/docs</code></p>
            </form>

            <div className="space-y-3 md:col-span-7" data-testid="admin-partners-list">
              {revealedKey && (
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4" data-testid="partner-key-reveal">
                  <p className="text-xs font-semibold text-emerald-800">{t("admin.keyShownOnce")}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 break-all rounded bg-white px-3 py-2 text-xs" data-testid="partner-key-value">{revealedKey.key}</code>
                    <Button size="sm" variant="outline" data-testid="partner-key-copy" onClick={() => copyKey(revealedKey.key)}>{t("admin.copy")}</Button>
                    <Button size="sm" variant="ghost" data-testid="partner-key-dismiss" onClick={() => setRevealedKey(null)}>✕</Button>
                  </div>
                </div>
              )}
              {partners.map((p) => {
                const id = partnerId(p);
                const rows = partnerBookings[id];
                return (
                  <div key={id} data-testid={`admin-partner-${id}`} className="rounded-lg border border-slate-200 bg-white p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium">{p.name}{p.company ? ` · ${p.company}` : ""}</p>
                        <p className="text-xs text-slate-500">
                          {p.contact_email || "—"} · {t("admin.pricingMode")}: {p.pricing_mode || "standard"}
                          {Number(p.markup_pct) ? ` (+${p.markup_pct}%)` : ""} · {t("admin.credit")} {eur(p.credit_limit || 0)}
                        </p>
                        {(p.api_key || p.api_key_preview) && (
                          <p className="mt-1 text-xs text-slate-400">{t("admin.apiKey")}: <code className="rounded bg-slate-100 px-1">{p.api_key || p.api_key_preview}</code></p>
                        )}
                        <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${p.active === false ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>
                          {p.active === false ? t("admin.inactive") : t("admin.active")}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" data-testid={`partner-rotate-${id}`} onClick={() => rotatePartnerKey(p)}>{t("admin.rotateKey")}</Button>
                        <Button size="sm" variant="outline" data-testid={`partner-bookings-${id}`} onClick={() => loadPartnerBookings(p)}>{t("admin.viewBookings")}</Button>
                        <Button size="sm" variant="outline" data-testid={`partner-toggle-${id}`} onClick={() => togglePartner(p)}>{p.active === false ? t("admin.enable") : t("admin.disable")}</Button>
                        <Button size="sm" variant="outline" className="border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`partner-delete-${id}`} onClick={() => { if (window.confirm(t("admin.confirmDelete"))) deletePartner(p); }}>{t("admin.delete")}</Button>
                      </div>
                    </div>
                    {rows && (
                      <div className="mt-4 border-t border-slate-100 pt-3" data-testid={`partner-bookings-list-${id}`}>
                        {rows.length === 0 && <p className="text-xs text-slate-500">{t("common.empty")}</p>}
                        {rows.map((b) => (
                          <div key={b.booking_id || b.code} className="flex justify-between py-1 text-xs">
                            <span className="text-slate-600">{b.code} · {b.origin?.name} → {b.destination?.name}</span>
                            <span className="font-medium">{eur(b.price_eur)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {partners.length === 0 && <p className="text-slate-500" data-testid="partners-empty">{t("common.empty")}</p>}
            </div>
          </div>
        )}

        {tab === "returns" && (
          <div className="mt-8 space-y-3" data-testid="admin-returns-list">
            {pendingReturns.map((r) => (
              <div key={r.booking_id || r.code} data-testid={`admin-return-${r.booking_id || r.code}`} className="rounded-lg border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{r.code} · {r.origin?.name || r.return_origin?.name} → {r.destination?.name || r.return_destination?.name}</p>
                    <p className="text-xs text-slate-500">{t("bookings.returnPickup")}: {(r.return_pickup_at || "").replace("T", " · ").slice(0, 18) || "—"}{r.return_flight_number ? ` · ${r.return_flight_number}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select data-testid={`return-vendor-${r.booking_id}`} defaultValue="" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" id={`retsel-${r.booking_id}`}>
                      <option value="" disabled>{t("ops.assign")}</option>
                      {vendors.filter((v) => v.approval_status === "approved").map((v) => (
                        <option key={v.user_id} value={v.user_id}>{v.name || v.email}</option>
                      ))}
                    </select>
                    <Button size="sm" className="bg-sea hover:bg-sea-dark" data-testid={`assign-return-${r.booking_id}-btn`} onClick={() => { const sel = document.getElementById(`retsel-${r.booking_id}`); if (sel?.value) assignReturn(r.booking_id, sel.value); else toast.error(t("ops.assign")); }}>
                      {t("ops.assign")}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {pendingReturns.length === 0 && <p className="text-slate-500" data-testid="returns-empty">{t("common.empty")}</p>}
          </div>
        )}

        {tab === "cancellations" && (
          <div className="mt-8 space-y-3" data-testid="admin-cancellations-list">
            {cancellations.map((c, i) => (
              <div key={c.id || c.booking_id || c.code || i} data-testid={`admin-cancellation-${c.code || i}`} className="rounded-lg border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{c.code || c.booking_code || "—"}{(c.origin?.name || c.destination?.name) ? ` · ${c.origin?.name || "?"} → ${c.destination?.name || "?"}` : ""}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {t("admin.cancelReason")}: <b>{t(`admin.reason_${c.reason_code}`) !== `admin.reason_${c.reason_code}` ? t(`admin.reason_${c.reason_code}`) : (c.reason_code || c.reason || "—")}</b>
                      {c.note ? ` · ${c.note}` : ""}
                    </p>
                    {(c.cancelled_by || c.actor || c.by) && <p className="text-xs text-slate-400">{c.cancelled_by || c.actor || c.by}</p>}
                  </div>
                  <span className="text-xs text-slate-400">{(c.cancelled_at || c.created_at || "").replace("T", " · ").slice(0, 16)}</span>
                </div>
              </div>
            ))}
            {cancellations.length === 0 && <p className="text-slate-500" data-testid="cancellations-empty">{t("common.empty")}</p>}
          </div>
        )}

        {tab === "invoices" && (
          <div className="mt-8">
            <div>
              <Label className="text-xs">{t("admin.month")}</Label>
              <Input data-testid="invoice-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5 max-w-xs" />
              {period && <p className="mt-2 text-xs text-slate-500" data-testid="invoice-period">{t("admin.month")}: {period}</p>}
            </div>
            <div className="mt-6 space-y-3" data-testid="admin-invoices-list">
              {invoices.map((inv, i) => (
                <div key={inv.invoice_id || i} data-testid={`admin-invoice-${i}`} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5">
                  <div>
                    <p className="text-sm font-medium">{inv.company || inv.agency_company || "—"}</p>
                    <p className="text-xs text-slate-500">{inv.month || month} · {inv.bookings ?? inv.count ?? 0} {t("admin.bookings")}</p>
                  </div>
                  <p className="font-display text-xl text-sea">{eur(inv.total_eur ?? inv.total ?? 0)}</p>
                </div>
              ))}
              {invoices.length === 0 && <p className="text-slate-500">{t("common.empty")}</p>}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) setCancelTarget(null); }}>
        <DialogContent data-testid="cancel-booking-dialog">
          <DialogHeader>
            <DialogTitle>{t("admin.cancelTitle")}{cancelTarget ? ` · ${cancelTarget.code}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">{t("admin.cancelReason")}</Label>
              <select
                data-testid="cancel-reason-select"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="mt-1.5 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              >
                {CANCEL_REASONS.map((r) => (
                  <option key={r} value={r}>{t(`admin.reason_${r}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">{t("admin.cancelNote")}</Label>
              <textarea
                data-testid="cancel-note-input"
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
                rows={3}
                maxLength={500}
                className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-sea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-dialog-dismiss" onClick={() => setCancelTarget(null)}>{t("admin.cancelDismiss")}</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" data-testid="cancel-dialog-confirm" onClick={submitCancel}>{t("admin.cancelSubmit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
